// @ts-check
import router from '@/router'
import { OAuthFlowTypeEnum } from '@/lib/app.model'
import { throwForbiddenAction, logAndRethrow, cookiesEnabled, isNativeFlowFromUrl } from '@/lib/util'
import {
	RouteAccess,
	RouteAccount,
	RouteLogin,
	RouteOAuthComplete,
	RouteSignUp,
} from '@/router/routes'
import Cookies from 'js-cookie'
import {
	COOKIES_JWT_KEY,
	COOKIES_REFRESH_KEY,
	COOKIES_CREDENTIALS_KEY,
	COOKIES_REFRESH_EXP_KEY,
} from '@/lib/api.model'
import { AuthFlowPayloadIntent } from '../state'

/**
 * @param {Record<string, any>} rootGetters
 * @returns {boolean}
 */
const isNativeFlowFromGetters = (rootGetters) => {
	const payload = rootGetters.authFlowPayload || {}
	const protocol = payload.protocol ?? ''
	const origin = payload.originPlatform ?? payload.origin_platform
	return origin === 'native' || protocol.startsWith('escs-external-')
}

/**
 * @param {Record<string, any>} rootGetters
 * @returns {boolean}
 */
const isNativeFlow = (rootGetters) => {
	const route = router.currentRoute.value
	const isNative = isNativeFlowFromUrl() || isNativeFlowFromGetters(rootGetters)
	return isNative
}

/** @type {import('./types').ActionTree} */
export default {
	loadUserCredentials({ dispatch, rootGetters }) {

		if (cookiesEnabled() && !isNativeFlow(rootGetters)) {
			const authToken = Cookies.get(COOKIES_JWT_KEY)
			const refreshToken = Cookies.get(COOKIES_REFRESH_KEY)
			const credentialsId = Cookies.get(COOKIES_CREDENTIALS_KEY)
			const refreshDeadline = Cookies.get(COOKIES_REFRESH_EXP_KEY)

			dispatch(
				'updateAuth',
				{ authToken, refreshToken, credentialsId, refreshDeadline },
				{ root: true }
			)
		}
	},

	updateUserCredentials({ rootGetters }, { authToken, refreshToken, credentialsId, refreshDeadline }) {
		if (cookiesEnabled() && !isNativeFlow(rootGetters)) {
			console.assert(authToken)
			console.assert(refreshToken)
			console.assert(refreshDeadline)

			/** @type { import('js-cookie').CookieAttributes } */
			const cookiesOptions = {
				sameSite: 'strict',
				secure: true,
				expires: 30	/* day */,
			}

			Cookies.set(COOKIES_JWT_KEY, authToken, cookiesOptions)
			Cookies.set(COOKIES_REFRESH_KEY, refreshToken, cookiesOptions)
			Cookies.set(COOKIES_CREDENTIALS_KEY, credentialsId, cookiesOptions)
			Cookies.set(COOKIES_REFRESH_EXP_KEY, refreshDeadline, cookiesOptions)
		}
	},

	removeUserCredentials({ rootGetters }) {
		if (cookiesEnabled() && !isNativeFlow(rootGetters)) {
			Cookies.remove(COOKIES_JWT_KEY)
			Cookies.remove(COOKIES_REFRESH_KEY)
			Cookies.remove(COOKIES_CREDENTIALS_KEY)
			Cookies.remove(COOKIES_REFRESH_EXP_KEY)
		}
	},

	async oauthSuccessRedirect({ dispatch, rootGetters }, {
		code = '',
		isSocialLogin = false,
		isFirstLogin = false,
	} = {}) {
		const {
			socialAuthRedirectUrl,
			redirectUrl,
		} = rootGetters.authFlowPayload
		const isRedirect = isSocialLogin
			? socialAuthRedirectUrl?.startsWith('http')
			: redirectUrl?.startsWith('http')

		if (!isRedirect) {
			router.replace({
				path: isSocialLogin ? socialAuthRedirectUrl : redirectUrl,
			})
			return
		}

		const url = new URL(
			isSocialLogin && socialAuthRedirectUrl != null
				? socialAuthRedirectUrl
				: redirectUrl,
		)
		url.searchParams.set('result', 'success')
		url.searchParams.set('code', code)
		url.searchParams.set('first_login', isFirstLogin)

		if (rootGetters.authFlowPayload.referralId) {
			url.searchParams.set('referralId', rootGetters.authFlowPayload.referralId)
		}

		const href = url.toString()
		const playerDomains = [
			'.player.test.escs.io',
			'.player.stage.escs.io',
			'.player.demo.escs.io',
			'.player.escs.io',
		]
		const isPlayerDomain = playerDomains.some((d) => href.includes(d))

		if (isPlayerDomain) {
			await dispatch('logout', { remoteLogout: true }, { root: true })
		}

		window.location.href = href
	},

	oauthFailureRedirect({ rootGetters }, { isSocialLogin = false } = {}) {
		const {
			socialAuthRedirectUrl,
			redirectUrl,
		} = rootGetters.authFlowPayload
		const url = new URL(
			isSocialLogin && socialAuthRedirectUrl != null
				? socialAuthRedirectUrl
				: redirectUrl,
		)
		url.searchParams.set('result', 'fail')
		window.location.href = url.toString()
	},

	oauthBackRedirect({ rootGetters }) {
		const url = new URL(rootGetters.authFlowPayload.redirectUrl)
		url.searchParams.set('result', 'back')
		window.location.href = url.toString()
	},

	/**
	 * @param {{ dispatch: Function, rootGetters: Record<string, any> }} param0
	 */
	async serviceOAuthFlow({ dispatch, rootGetters }, { isSocialLogin = false } = {}) {
		if (rootGetters.authFlowPayload == null) {
			throwForbiddenAction()
		}

		try {
			const {
				permissions,
				redirectUrl,
				applicationId,
			} = rootGetters.authFlowPayload
			const { code } = await dispatch(
				'grantAuthorizationCode',
				{ permissions, redirectUrl, applicationId },
				{ root: true }
			)

			if (code) {
				dispatch('oauthSuccessRedirect', { code, isSocialLogin })
			} else {
				throw new Error('Missing API response.')
			}
		} catch (error) {
			logAndRethrow(error)
		}
	},

	/**
	 * @param {{ dispatch: Function, rootGetters: Record<string, any> }} param0
	 */
	async basicOAuthFlow({ dispatch, rootGetters }, {
		isSocialLogin = false,
		isFirstLogin = false,
	} = {}) {
		if (rootGetters.authFlowPayload == null) {
			throwForbiddenAction()
		}

		try {
			if (!rootGetters.isAuthenticated) {
				await dispatch(
					'authorizeGameAccount',
					null,
					{ root: true }
				)
			}

			const {
				permissions,
				redirectUrl,
				applicationId,
				providerId,
			} = rootGetters.authFlowPayload
			const payload = {
				permissions,
				redirectUrl,
				applicationId: rootGetters.authFlowWithProvider
					? providerId
					: applicationId,
			}
			const { code } = await dispatch(
				'grantAuthorizationCode',
				payload,
				{ root: true }
			)

			if (code) {
				dispatch('oauthSuccessRedirect', { code, isSocialLogin, isFirstLogin })
			} else {
				throw new Error('Missing API response.')
			}
		} catch (error) {
			logAndRethrow(error)
		}
	},

	/**
	 * @param {import('./types').ActionContext} param0
	 * @param {import('@/store/state').AuthFlowPayload} payload
	 * @returns {Promise<void>}
	 */
	async startOAuthFlow({ dispatch, rootGetters }, payload) {
		const { type } = payload

		if (
			payload.providerId != null &&
			(
				type === OAuthFlowTypeEnum.Basic ||
				type === OAuthFlowTypeEnum.Integration ||
				type === OAuthFlowTypeEnum.Link
			)
		) {
			dispatch('setAuthFlowWithProvider', true, { root: true })
		}

		await router.isReady()

		// Integration flow is handled by the 3rd party OAuth service(on the backend).
		if (type === OAuthFlowTypeEnum.Integration) {
			if (rootGetters.isAuthenticated) {
				await dispatch('logout', { remoteLogout: true }, { root: true })
			}

			await dispatch('authorizeGameAccount', null, { root: true })
			router.replace({ name: RouteOAuthComplete })
			return
		}

		if (typeof rootGetters.authFlowPayload.ssoToken === 'string') {
			const payload = {
				token: rootGetters.authFlowPayload.ssoToken,
			}

			await dispatch('signInWithSSO', payload, { root: true })
			await dispatch('proceedOAuthFlow')
		}

		await router.replace({
			name: rootGetters.isAuthenticated
				? RouteAccount
				: payload.intent === AuthFlowPayloadIntent.SIGNUP
					? RouteSignUp
					: RouteLogin
		})
	},

	async checkPermissions({ dispatch }) {
		const needsUserAction = await dispatch(
			'checkAuthNeedsUserAction',
			null,
			{ root: true }
		)

		if (!needsUserAction) {
			return true
		}

		const applicationIsBoundToUser = await dispatch(
			'checkApplicationIsBoundToUser',
			null,
			{ root: true }
		)

		return applicationIsBoundToUser
	},

	/**
	 * @returns {Promise<void>}
	 */
	async proceedOAuthFlow({ rootGetters, dispatch }, {
		isSocialLogin = false,
		isFirstLogin = false,
	} = {}) {
		const authFlowPayload = rootGetters.authFlowPayload
		const authFlowType = rootGetters.authFlowType

		await router.isReady()

		if (!authFlowPayload) {
			await router.replace({ name: RouteLogin })
			return
		}

		if (
			authFlowType === OAuthFlowTypeEnum.Basic ||
			authFlowType === OAuthFlowTypeEnum.Service
		) {
			const hasPermissions = await dispatch('checkPermissions')

			if (!hasPermissions) {
				await router.replace({ name: RouteAccess })
				return
			}
		}

		if (authFlowType === OAuthFlowTypeEnum.Basic) {
			await dispatch('basicOAuthFlow', { isSocialLogin, isFirstLogin })
			return
		}

		if (authFlowType === OAuthFlowTypeEnum.Service) {
			await dispatch('serviceOAuthFlow', { isSocialLogin, isFirstLogin })
			return
		}

		if (authFlowType === OAuthFlowTypeEnum.Link) {
			await dispatch('basicOAuthFlow', { isSocialLogin, isFirstLogin })
			return
		}
	},

	async socialLogin({ dispatch }, { type, code, user }) {
		const MONTH_MS = 30 * 24 * 60 * 60 * 1e3
		const res = await dispatch(
			'socialLogin',
			{
				type,
				code,
				user,
			},
			{ root: true },
		)

		if (
			res.service.action.type === 'LOGIN' &&
			'session' in res.service.action
		) {
			const {
				session: { authToken, refreshToken },
				isFirstLogin,
			} = res.service.action
			const refreshDeadline = Date.now() + MONTH_MS
			const authPayload = {
				authToken,
				refreshToken,
				refreshDeadline,
			}

			dispatch('updateAuth', authPayload, { root: true })
			dispatch('updateUserCredentials', authPayload)
			await dispatch('loadUser', null, { root: true })

			return {
				isFirstLogin,
			}
		}

		return {}
	},
}
